import graphQLWebservice from './graphQLWebservice';
import webserviceAjaxIds from './webserviceAjaxIds';
import loggedInUserQuery from './graphql/query/loggedInUserQuery';
import oneAtATime from './oneAtATime';
import listingsQuery from './graphql/query/listingsQuery';
import saveDrugMutation from './graphql/mutation/saveDrugMutation';
import deleteDrugMutation from './graphql/mutation/deleteDrugMutation';
import wacsQuery from './graphql/query/wacsQuery';
import saveWacMutation from './graphql/mutation/saveWacMutation';
import deleteWacMutation from './graphql/mutation/deleteWacMutation';
import deleteApprovalMutation from './graphql/mutation/deleteApprovalMutation';
import deletePatentMutation from './graphql/mutation/deletePatentMutation';
import wacsByNDC from './graphql/query/wacsByNDC';
import usersQuery from './graphql/query/usersQuery';
import saveUser from './graphql/mutation/saveUserMutation';
import deleteUserMutation from './graphql/mutation/deleteUserMutation';
import companiesQuery from './graphql/query/companiesQuery';
import saveCompanyMutation from './graphql/mutation/saveCompanyMutation';
import deleteCompanyMutation from './graphql/mutation/deleteCompanyMutation';
import companySingleQuery from './graphql/query/companySingleQuery';
import userSingleQuery from './graphql/query/userSingleQuery';
import savePatentMutation from './graphql/mutation/savePatentMutation';
import saveApprovalMutation from './graphql/mutation/saveApprovalMutation';
import userPerEmail from './graphql/query/userPerEmail';
import drugApprovalPerOrg from './graphql/query/drugApprovalPerOrg';
import patentLostPerOrg from './graphql/query/patentLostPerOrg';
import wacsPerOrg from './graphql/query/wacsPerOrg';
import usersPerOrg from './graphql/query/usersPerOrg';
import approveCompany from './graphql/mutation/approveCompanyMutation';
import submitCompanyMutation from './graphql/mutation/submitCompanyMutation';

import submitUserMutation from './graphql/mutation/submitUserMutation';
import inactivateCompanyMutation from './graphql/mutation/inactivateCompanyMutation';
import debugQuery from './graphql/query/debugQuery';
import versionInfoQuery from './graphql/query/versionInfoQuery';

export default {
  system: {
    // Public DEBUG flag (the server DEBUG env var) used to gate the in-browser
    // devLogger. Returns true only when the server has DEBUG explicitly true.
    debug: () =>
      graphQLWebservice
        .query(debugQuery())
        .then((result) => result.data.debug),
    // Public API build/deploy info (version + ISO deploy datetime) for the
    // footer. No auth required (mirrors the server versionInfo resolver).
    versionInfo: () =>
      graphQLWebservice
        .query(versionInfoQuery())
        .then((result) => result.data.versionInfo),
  },
  user: {
    loggedInUser: () =>
      oneAtATime(
        () =>
          graphQLWebservice
            .query(loggedInUserQuery(), webserviceAjaxIds.user.loggedInUser)
            .then((result) => result.data.loggedInUser),
        webserviceAjaxIds.user.loggedInUser,
        'loggedInUser',
      ),
    get: (email) =>
      graphQLWebservice
        .query(userSingleQuery(email))
        .then((result) => result.data),
    perEmail: (email) =>
      graphQLWebservice
        .query(userPerEmail(email))
        .then((result) => result.data),
    all: (orgId) =>
      graphQLWebservice.query(usersQuery(orgId)).then((result) => result.data),
    perOrg: (orgId) =>
      graphQLWebservice.query(usersPerOrg(orgId)).then((result) => result.data),
    save: (user) =>
      graphQLWebservice.mutation(saveUser(user)).then((result) => result.data),
    delete: (user) =>
      graphQLWebservice
        .mutation(deleteUserMutation(user))
        .then((result) => result.data),
    submit: (user) =>
      graphQLWebservice
        .mutation(submitUserMutation(user))
        .then((result) => result.data),
  },
  drug: {
    all: (orgId) =>
      graphQLWebservice
        .query(listingsQuery(orgId))
        .then((result) => result.data),
    save: (drug) =>
      graphQLWebservice
        .mutation(saveDrugMutation(drug))
        .then((result) => result.data),
    delete: (drug) => {
      console.log('[Webservice] drug.delete called with drug:', drug);
      console.log('[Webservice] Drug ID:', drug?.id);

      return graphQLWebservice
        .mutation(deleteDrugMutation(drug))
        .then((result) => {
          console.log(
            '[Webservice] drug.delete mutation successful, result:',
            result,
          );
          return result.data;
        })
        .catch((error) => {
          console.error('[Webservice] drug.delete mutation failed:', error);
          console.error('[Webservice] Error details:', {
            message: error.message,
            stack: error.stack,
            drugInput: drug,
          });
          throw error;
        });
    },
  },
  wac: {
    all: () => {
      console.log('[Webservice] wac.all() called');
      return graphQLWebservice
        .query(wacsQuery())
        .then((result) => {
          console.log('[Webservice] wac.all() successful, result:', result);
          console.log('[Webservice] Result data:', result.data);
          return result.data;
        })
        .catch((error) => {
          console.error('[Webservice] wac.all() failed:', error);
          console.error('[Webservice] Error details:', {
            message: error.message,
            stack: error.stack,
          });
          throw error;
        });
    },
    perDrug: (ndc) =>
      graphQLWebservice.query(wacsByNDC(ndc)).then((result) => result.data),
    perOrg: (org) =>
      graphQLWebservice.query(wacsPerOrg(org)).then((result) => result.data),
    save: (wac) => {
      console.log('[Webservice] wac.save() called with:', wac);
      return graphQLWebservice
        .mutation(saveWacMutation(wac))
        .then((result) => {
          console.log('[Webservice] wac.save() successful, result:', result);
          return result.data;
        })
        .catch((error) => {
          console.error('[Webservice] wac.save() failed:', error);
          throw error;
        });
    },
    patentLostPerOrg: (orgId) =>
      graphQLWebservice
        .query(patentLostPerOrg(orgId))
        .then((result) => result.data),
    savePatent: (patent) =>
      graphQLWebservice
        .mutation(savePatentMutation(patent))
        .then((result) => result.data),
    drugApprovalPerOrg: (orgId) =>
      graphQLWebservice
        .query(drugApprovalPerOrg(orgId))
        .then((result) => result.data),
    saveApproval: (approval) =>
      graphQLWebservice
        .mutation(saveApprovalMutation(approval))
        .then((result) => result.data),
    deleteApproval: (approval) => {
      console.log(
        '[Webservice] deleteApproval called with approval:',
        approval,
      );
      console.log('[Webservice] Approval ID:', approval?.id);

      return graphQLWebservice
        .mutation(deleteApprovalMutation(approval))
        .then((result) => {
          console.log(
            '[Webservice] deleteApproval mutation successful, result:',
            result,
          );
          console.log('[Webservice] Result data:', result.data);
          return result.data;
        })
        .catch((error) => {
          console.error('[Webservice] deleteApproval mutation failed:', error);
          console.error('[Webservice] Error details:', {
            message: error.message,
            stack: error.stack,
            approvalInput: approval,
          });
          throw error;
        });
    },
    deletePatent: (patent) => {
      console.log('[Webservice] deletePatent called with patent:', patent);
      console.log('[Webservice] Patent ID:', patent?.id);

      return graphQLWebservice
        .mutation(deletePatentMutation(patent))
        .then((result) => {
          console.log(
            '[Webservice] deletePatent mutation successful, result:',
            result,
          );
          console.log('[Webservice] Result data:', result.data);
          return result.data;
        })
        .catch((error) => {
          console.error('[Webservice] deletePatent mutation failed:', error);
          console.error('[Webservice] Error details:', {
            message: error.message,
            stack: error.stack,
            patentInput: patent,
          });
          throw error;
        });
    },
    deleteWac: (wac) => {
      console.log('[Webservice] deleteWac called with wac:', wac);
      console.log('[Webservice] WAC ID:', wac?.id);

      return graphQLWebservice
        .mutation(deleteWacMutation(wac))
        .then((result) => {
          console.log(
            '[Webservice] deleteWac mutation successful, result:',
            result,
          );
          console.log('[Webservice] Result data:', result.data);
          return result.data;
        })
        .catch((error) => {
          console.error('[Webservice] deleteWac mutation failed:', error);
          console.error('[Webservice] Error details:', {
            message: error.message,
            stack: error.stack,
            wacInput: wac,
          });
          throw error;
        });
    },
  },
  company: {
    get: (fein) =>
      graphQLWebservice
        .query(companySingleQuery(fein))
        .then((result) => result.data.org),
    all: () =>
      graphQLWebservice.query(companiesQuery()).then((result) => result.data),
    save: (company) =>
      graphQLWebservice
        .mutation(saveCompanyMutation(company))
        .then((result) => result.data),
    delete: (company) =>
      graphQLWebservice
        .mutation(deleteCompanyMutation(company))
        .then((result) => result.data),
    inactivate: (id) =>
      graphQLWebservice.mutation(inactivateCompanyMutation(id)),
    approve: (ordId) =>
      graphQLWebservice
        .mutation(approveCompany(ordId))
        .then((result) => result.data),
    submit: (company) =>
      graphQLWebservice
        .mutation(submitCompanyMutation(company))
        .then((result) => result.data),
  },
};
